import React, { useState } from 'react';
import { Phone, Mail, Menu, X, Clock, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';
import { trackPixel } from '../lib/pixel';
import BookingCalendar from './BookingCalendar';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [showBookingDropdown, setShowBookingDropdown] = useState(false);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Team', href: '#team' },
    { name: 'Locations', href: '#locations' },
    { name: 'Reviews', href: '#reviews' },
    { name: 'Contact', href: '#contact' },
    { name: 'My Bookings', href: '/my-bookings', external: true },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Elegant Top Bar */}
      <div className="bg-luxury-green text-white/80 py-2.5 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-8">
            <a href={`tel:${businessInfo.phone}`} onClick={() => trackPixel('Contact', { content_name: 'Top Bar Phone', method: 'phone' })} className="flex items-center gap-2 hover:text-luxury-gold transition-colors text-xs tracking-widest uppercase">
              <Phone size={12} strokeWidth={1.5} />
              <span>{businessInfo.phone}</span>
            </a>
            <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 hover:text-luxury-gold transition-colors text-xs tracking-widest uppercase">
              <Mail size={12} strokeWidth={1.5} />
              <span>{businessInfo.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-2 text-xs tracking-widest uppercase">
            <Clock size={12} strokeWidth={1.5} />
            <span>{businessInfo.hours}</span>
          </div>
        </div>
      </div>

      {/* Glass Navbar */}
      <nav className="bg-white/80 backdrop-blur-xl sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <a href="#home" onClick={(e) => scrollToSection(e, '#home')} className="flex flex-col">
              <span className="font-display text-2xl font-medium text-luxury-green tracking-tight">Dublin Holistic</span>
              <span className="text-[10px] text-luxury-gold font-medium tracking-[0.3em] uppercase">Massage & Wellness</span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-xs font-medium uppercase tracking-[0.15em] text-stone-500 hover:text-luxury-green transition-colors relative group"
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-luxury-gold transition-all duration-500 group-hover:w-full" />
                  </a>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-xs font-medium uppercase tracking-[0.15em] text-stone-500 hover:text-luxury-green transition-colors relative group"
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-luxury-gold transition-all duration-500 group-hover:w-full" />
                  </a>
                )
              ))}
            </div>

            {/* CTA */}
            <div className="hidden lg:flex items-center relative">
              <div
                className="relative"
                onMouseEnter={() => setShowBookingDropdown(true)}
                onMouseLeave={() => setShowBookingDropdown(false)}
              >
                <Button
                  className="bg-luxury-green hover:bg-luxury-green/90 text-white px-8 py-5 rounded-full text-xs uppercase tracking-[0.2em] font-medium transition-all hover:scale-105 shadow-lg shadow-luxury-green/20 flex items-center gap-2"
                >
                  Book Now
                  <ChevronDown size={14} strokeWidth={1.5} />
                </Button>
                <div className={`absolute top-full right-0 mt-3 w-60 bg-white rounded-2xl shadow-2xl border border-stone-100 overflow-hidden transition-all duration-300 ${showBookingDropdown ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <button
                    onClick={() => { setIsBookingOpen(true); setShowBookingDropdown(false); }}
                    className="w-full px-5 py-4 text-left hover:bg-bone transition-colors"
                  >
                    <p className="font-display text-sm text-luxury-green">Book Online</p>
                    <p className="text-[10px] text-stone-400 tracking-wide uppercase mt-0.5">With loyalty rewards</p>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Menu */}
            <button className="lg:hidden p-2 text-luxury-green" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden absolute w-full bg-white/95 backdrop-blur-xl shadow-2xl border-t border-stone-100 transition-all duration-300 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <div className="px-6 py-8 space-y-1">
            {navLinks.map((link) => (
              link.external ? (
                <a key={link.name} href={link.href}
                  className="block text-sm font-medium uppercase tracking-[0.15em] text-stone-500 hover:text-luxury-green py-3 transition-colors border-b border-stone-100">
                  {link.name}
                </a>
              ) : (
                <a key={link.name} href={link.href} onClick={(e) => scrollToSection(e, link.href)}
                  className="block text-sm font-medium uppercase tracking-[0.15em] text-stone-500 hover:text-luxury-green py-3 transition-colors border-b border-stone-100">
                  {link.name}
                </a>
              )
            ))}
            <div className="pt-4">
              <Button
                onClick={() => { setIsBookingOpen(true); setIsMenuOpen(false); }}
                className="w-full bg-luxury-green hover:bg-luxury-green/90 text-white rounded-full py-5 text-xs uppercase tracking-[0.2em] font-medium"
              >
                Book Your Experience
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <BookingCalendar isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </>
  );
};

export default Navbar;
